import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Avatar,
  Typography,
  Chip,
  Box,
  Stack,
  IconButton,
  Grid,
  Modal,
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningIcon from "@mui/icons-material/Warning";
import Dashboard from "layouts/dashboard";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PropTypes from "prop-types";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { Link } from "react-router-dom";
import link from "assets/theme/components/link";
import colors from "assets/theme/base/colors";
import { FaAngleRight } from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "service";
import Lottie from "lottie-react";
import runningPersonAnimation from "./running-person.json";
import useAuthStore from "store/authStore";

const Table = () => {
  const [cards, setCardsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const { email } = useAuthStore();
  const [visitedLinks, setVisitedLinks] = useState(
    useMemo(() => {
      const stored = localStorage.getItem("visitedLinks");
      if (stored) {
        const { date, links, tasksSubmitted, email: storedEmail } = JSON.parse(stored);
        const today = new Date().toDateString();
        if (date === today && storedEmail === email) {
          return { links, tasksSubmitted };
        }
      }
      return { links: {}, tasksSubmitted: false };
    }, [email])
  );
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [timer, setTimer] = useState(60);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false); // 🔹 added: flag to avoid multiple retries

  const tasksCount = useMemo(() => Object.keys(visitedLinks.links).length, [visitedLinks.links]);
  const isSubmitted = useMemo(() => visitedLinks.tasksSubmitted, [visitedLinks]);

  const formattedDate = useMemo(() => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();
    return `${day}${month}${year}`; // e.g., "21062025"
  }, []);

  const getVisitedLinks = () => {
    try {
      const stored = localStorage.getItem("visitedLinks");
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      if (
        parsed &&
        typeof parsed === "object" &&
        parsed.links &&
        typeof parsed.links === "object" &&
        parsed.email === email
      ) {
        return parsed;
      }
      return null;
    } catch (err) {
      console.warn("Invalid visitedLinks in localStorage:", err);
      return null;
    }
  };

  // 🔹 New function: fetch earnings with retry check
  const fetchEarnings = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/earning/dailyselfearning/?email=${encodeURIComponent(email)}`
      );
      const data = response.data.earnings || {};
      console.log("Earnings data:", data);
      const tasksResponse = await axios.get(`${BASE_URL}/api/dailyTasks/tasksByUser/${email}`, {
        headers: { "Content-Type": "application/json" },
      });
      const tasksData = tasksResponse.data.data;

      const tasks = JSON.parse(tasksData.tasks);
      const todayTask = tasks.find((task) => task.hasOwnProperty(formattedDate));

      if (
        Number(data.daily_self_earning) === 0 &&
        todayTask &&
        todayTask[formattedDate] &&
        visitedLinks.tasksSubmitted
      ) {
        const localData = getVisitedLinks();
        console.log("Local visitedLinks data:", localData);
        if (
          localData &&
          Object.keys(localData.links).length === 20 &&
          localData.tasksSubmitted === true
        ) {
          console.log("Earnings = 0 but tasks are done → retrying POST...");
          await retryPostEarning();
        }
      }
    } catch (err) {
      console.error("Error fetching earnings:", err);
    }
  };

  // 🔹 New function: retry POST earning
  const retryPostEarning = async () => {
    if (isRetrying) return;
    setIsRetrying(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/api/earning/dailyselfearning?email=${encodeURIComponent(email)}`,
        {},
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.status === 200) {
        console.log("Retry POST successful, re-fetching earnings...");
        await fetchEarnings();
      } else {
        console.warn("Unexpected POST response:", res.status, res.data);
      }
    } catch (err) {
      console.error("Error retrying POST earning:", err);
    } finally {
      setIsRetrying(false);
    }
  };

  const fetchCardsAndTasks = async () => {
    try {
      setIsLoading(true);
      const cardsResponse = await axios.get(`${BASE_URL}/api/link/get/all`);
      console.log("cardsResponse___--", cardsResponse);
      const fetchedData = cardsResponse.data.data.map((item) => ({
        id: item.id,
        task: item.task,
        task_link: item.task,
      }));
      setCardsData(fetchedData);

      const tasksResponse = await axios.get(`${BASE_URL}/api/dailyTasks/tasksByUser/${email}`, {
        headers: { "Content-Type": "application/json" },
      });

      const tasksData = tasksResponse.data.data;

      if (tasksData && tasksData.email === email) {
        // 🔹 FIX: Check if tasks is already an object or needs parsing
        const tasks =
          typeof tasksData.tasks === "string" ? JSON.parse(tasksData.tasks) : tasksData.tasks;

        // console.log(tasks, "----", formattedDate, "tasksData____", tasksData.tasks);

        const todayTask = tasks.find((task) => task.hasOwnProperty(formattedDate));

        if (todayTask && todayTask[formattedDate] && !visitedLinks.tasksSubmitted) {
          const allLinks = {};
          for (let i = 1; i <= 20; i++) {
            allLinks[i] = true;
          }
          setVisitedLinks({
            links: allLinks,
            tasksSubmitted: true,
          });
          localStorage.setItem(
            "visitedLinks",
            JSON.stringify({
              date: new Date().toDateString(),
              email,
              links: allLinks,
              tasksSubmitted: true,
            })
          );
        }
      }
      // 🔹 added: after fetching tasks, also check earnings
      await fetchEarnings();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCardsAndTasks();
  }, [email]);

  useEffect(() => {
    const checkDateAndTime = () => {
      const now = new Date();
      const today = now.toDateString();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();

      if (hours === 23 && minutes === 59 && seconds >= 0 && tasksCount === 20 && isSubmitted) {
        setVisitedLinks({ links: {}, tasksSubmitted: false });
        localStorage.setItem(
          "visitedLinks",
          JSON.stringify({
            date: today,
            email,
            links: {},
            tasksSubmitted: false,
          })
        );
      }

      const storedLinks = localStorage.getItem("visitedLinks");
      if (storedLinks) {
        const { date, email: storedEmail } = JSON.parse(storedLinks);
        if (date !== today || storedEmail !== email) {
          setVisitedLinks({ links: {}, tasksSubmitted: false });
          localStorage.setItem(
            "visitedLinks",
            JSON.stringify({
              date: today,
              email,
              links: {},
              tasksSubmitted: false,
            })
          );
        }
      } else {
        localStorage.setItem(
          "visitedLinks",
          JSON.stringify({
            date: today,
            email,
            links: {},
            tasksSubmitted: false,
          })
        );
      }
    };

    checkDateAndTime();
    const interval = setInterval(checkDateAndTime, 1000);
    return () => clearInterval(interval);
  }, [email, tasksCount, isSubmitted]);

  useEffect(() => {
    let interval;
    if (showTimerModal && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setShowTimerModal(false);
            handleLinkClick(selectedTaskId);
            setSelectedTaskId(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showTimerModal, timer, selectedTaskId]);

  const handleLinkClick = (taskId) => {
    const newVisitedLinks = {
      ...visitedLinks,
      links: {
        ...visitedLinks.links,
        [taskId]: true,
      },
    };
    setVisitedLinks(newVisitedLinks);
    localStorage.setItem(
      "visitedLinks",
      JSON.stringify({
        date: new Date().toDateString(),
        email,
        links: newVisitedLinks.links,
        tasksSubmitted: newVisitedLinks.tasksSubmitted,
      })
    );
  };

  const handleSubmitTasks = async () => {
    if (tasksCount === 20 && !isSubmitted) {
      setIsSubmitting(true); // Show loading state
      try {
        // Step 1: Update daily tasks
        const tasksResponse = await axios.put(
          `${BASE_URL}/api/dailyTasks/tasks`,
          { email },
          { headers: { "Content-Type": "application/json" } }
        );

        console.log("PUT Response:", tasksResponse);
        console.log("Earning call starting now...");

        // Step 2: Call earning API if tasks count is 20 and email matches
        const earningResponse = await axios.post(
          `${BASE_URL}/api/earning/dailyselfearning?email=${email}`,
          {},
          { headers: { "Content-Type": "application/json" } }
        );

        console.log("Done call starting now++++++++++");

        if (earningResponse.status === 200) {
          console.log("______", earningResponse);
          // Update state and localStorage
          setVisitedLinks((prev) => {
            const updatedLinks = {
              ...prev,
              tasksSubmitted: true,
            };
            localStorage.setItem(
              "visitedLinks",
              JSON.stringify({
                date: new Date().toDateString(),
                email,
                links: updatedLinks.links,
                tasksSubmitted: true,
              })
            );
            return updatedLinks;
          });
          if (tasksResponse.status !== 201) {
            throw new Error(`Unexpected tasks response status: ${tasksResponse.status}`);
          }

          // Show success notification
          setSnackbarSeverity("success");
          setSnackbarMessage("Daily tasks completed and earnings credited!");
          setOpenSnackbar(true);
        } else {
          throw new Error(`Unexpected earning response status: ${earningResponse.status}`);
        }

        // Refresh data
        await fetchCardsAndTasks();
      } catch (error) {
        console.error("Error submitting tasks or recording earnings:", error);
        setSnackbarSeverity("error");
        setSnackbarMessage(`Error: ${error.message}. Please try again.`);
        setOpenSnackbar(true);
      } finally {
        setIsSubmitting(false); // Exit loading state
      }
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const ProjectCard = ({ task, id, task_link }) => {
    const isVisited = visitedLinks.links[id];
    const isDisabled = selectedTaskId && selectedTaskId !== id;

    const handleCardClick = (e) => {
      e.preventDefault();
      if (isDisabled || isVisited) return;

      setSelectedTaskId(id);
      setTimer(60);
      setShowTimerModal(true);
      window.open(task_link, "_blank");
    };

    return (
      <div>
        <a href={task_link} onClick={handleCardClick}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "white",
              padding: "15px",
              borderRadius: "10px",
              boxShadow: "rgba(0, 0, 0, 0.10) 0px 5px 15px",
              opacity: isDisabled ? 0.5 : 1,
              pointerEvents: isDisabled ? "none" : "auto",
            }}
          >
            <div style={{ color: "#42424a" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <h4>Task: {id}</h4>
              </div>
              <div
                style={{
                  backgroundColor: isVisited ? "#4caf501a" : "#f443361a",
                  marginTop: "7px",
                  borderRadius: "5px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                }}
              >
                <p
                  style={{
                    fontSize: "14px",
                    color: isVisited ? "#4CAF50" : "#f44336",
                    fontWeight: "600",
                    textAlign: "center",
                  }}
                >
                  {isVisited ? "Seen" : "Unseen"}
                </p>
              </div>
            </div>
            <FaAngleRight color="#1A73E8" />
          </div>
        </a>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <h2 style={{ padding: "10px" }}>Your Daily Tasks</h2>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Lottie
            animationData={runningPersonAnimation}
            loop={true}
            style={{ width: 200, height: 200 }}
          />
        </Box>
      ) : (
        <>
          <Grid
            container
            spacing={2}
            justifyContent="space-evenly"
            sx={{ flexWrap: "wrap", padding: "0rem" }}
          >
            {cards.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <ProjectCard {...card} />
              </Grid>
            ))}
          </Grid>
          {(tasksCount === 20 || isSubmitted) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
                position: "relative",
              }}
            >
              <Button
                variant="contained"
                onClick={handleSubmitTasks}
                disabled={isSubmitted || tasksCount !== 20 || isSubmitting}
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.87)",
                  color: isSubmitted ? "#4CAF50" : "#fff",
                  pointerEvents: isSubmitted || isSubmitting ? "none" : "auto",
                  opacity: isSubmitted ? 0.9 : 1,
                  fontWeight: "bold",
                  minWidth: "150px",
                  height: "40px",
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Wait a moment...
                  </>
                ) : isSubmitted ? (
                  "Be ready for tomorrow Tasks"
                ) : (
                  "Submit Tasks"
                )}
              </Button>
            </Box>
          )}
          <Modal
            open={showTimerModal}
            onClose={() => {}}
            aria-labelledby="timer-modal-title"
            aria-describedby="timer-modal-description"
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 400,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
              }}
            >
              <Alert severity="warning" icon={<WarningIcon />}>
                <AlertTitle>Task Timer</AlertTitle>
                <Typography variant="body1">
                  Please spend at least 60 seconds on the task page. The task will only be marked as
                  seen after the timer completes.
                </Typography>
                <Typography variant="body1" sx={{ mt: 2, fontWeight: "bold" }}>
                  Time remaining: {timer} seconds
                </Typography>
                <Typography variant="body2" sx={{ mt: 2, color: "error.main" }}>
                  Warning: Missing any task will result in no payment for the day. Ensure all tasks
                  are completed and marked as seen.
                </Typography>
              </Alert>
            </Box>
          </Modal>
          <Snackbar
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </>
      )}
    </DashboardLayout>
  );
};

Table.propTypes = {
  task: PropTypes.string,
  id: PropTypes.number,
  bg: PropTypes.string,
  task_link: PropTypes.string,
};

export default Table;
